import { useCallback, useEffect, useState } from "react";
import type { Key } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import type { ColumnsType } from "antd/es/table/interface";

import { MetadataDashboardTemplate } from "@/components/Templates/MetadataDashboardTemplate";
import type { DashboardTableContext } from "@/components/Templates/types";
import { prepareUnifiedDataPostRequestBody } from "@/components/UI/TableToolbar/Filtering/filtersApiUtil";
import { useReviewMarkMutations } from "@/hooks/useReviewMarkMutations";
import medicalReviewApi, {
  MedicalReviewApiError,
  type DatasetRowsPage,
} from "@/services/medicalReviewApi";
import ReviewBulkActionBar from "./ReviewBulkActionBar";
import ReviewItemDrawer from "./ReviewItemDrawer";
import ReviewQueueRowActions from "./ReviewQueueRowActions";
import {
  MAX_BULK_MARKS,
  REVIEW_ITEMS_DATASET,
  reviewRowId,
  reviewRowText,
  toMarkTargets,
} from "./medicalReview";

const ITEM_PARAM = "item";

/** Reviewed content is identified by hash, so selection carries one per record. */
type Selection = Map<number, string>;

const selectionFromRows = (rows: Record<string, unknown>[]): Selection =>
  new Map(
    rows.map((row) => [reviewRowId(row), reviewRowText(row, "material_hash")]),
  );

export default function ReviewQueueTable({ trialId }: { trialId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openItemId, setOpenItemId] = useState<number | null>(
    () => Number(searchParams.get(ITEM_PARAM)) || null,
  );
  const [selection, setSelection] = useState<Selection>(() => new Map());
  const [tableContext, setTableContext] =
    useState<DashboardTableContext | null>(null);

  const clearSelection = useCallback(() => setSelection(new Map()), []);
  const {
    markItems,
    reopenItems,
    isMarking,
    reportUnmarkable,
    openErrorNotification,
    contextHolder,
  } = useReviewMarkMutations(trialId, clearSelection);

  useEffect(() => {
    if (!searchParams.get(ITEM_PARAM)) return;
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(ITEM_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const selectAllMatchingMutation = useMutation({
    mutationFn: (): Promise<DatasetRowsPage> =>
      medicalReviewApi.datasetRows(trialId, REVIEW_ITEMS_DATASET, {
        dataset_type: REVIEW_ITEMS_DATASET,
        ...prepareUnifiedDataPostRequestBody({
          page: 1,
          pageSize: MAX_BULK_MARKS,
          global_filters: tableContext?.filters ?? {},
          allFiltersData: tableContext?.filterMetadata,
        }),
      }),
    onSuccess: (page) => setSelection(selectionFromRows(page.results ?? [])),
    onError: (error: MedicalReviewApiError) =>
      openErrorNotification({
        title: "Could not select the matching records",
        description: error.message,
      }),
  });

  const submitMarks = (hashById: Selection, reopen = false) => {
    const { targets, unresolved } = toMarkTargets(hashById);
    if (unresolved.length) reportUnmarkable(unresolved.length);
    if (!targets.length) return;
    if (reopen) reopenItems(targets);
    else markItems(targets);
  };

  const extraColumns: ColumnsType<Record<string, unknown>> = [
    {
      key: "medical_review_action",
      title: "",
      width: 150,
      fixed: "right",
      render: (_: unknown, row: Record<string, unknown>) => (
        <ReviewQueueRowActions
          row={row}
          marking={isMarking}
          onMark={() => submitMarks(selectionFromRows([row]))}
          onReopen={() => submitMarks(selectionFromRows([row]), true)}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {contextHolder}

      <ReviewBulkActionBar
        selectedCount={selection.size}
        filteredCount={tableContext?.filteredCount ?? 0}
        marking={isMarking}
        selectingAll={selectAllMatchingMutation.isPending}
        onMarkSelected={() => submitMarks(selection)}
        onClearSelection={clearSelection}
      />

      <MetadataDashboardTemplate
        type={REVIEW_ITEMS_DATASET}
        constants={{
          wikiLink: "/wiki/guides/medical-review",
          idLink: undefined,
        }}
        rowKey="id"
        selectionIdentifierColumn="subject_id"
        extraColumns={extraColumns}
        onRow={(row) => ({
          onClick: () => setOpenItemId(reviewRowId(row)),
          style: { cursor: "pointer" },
        })}
        rowSelection={{
          // Its own column with room to breathe. Left to size itself the
          // checkbox shares space with the first data column, which squeezes
          // the subject id — the one value a reviewer scans down.
          columnWidth: 48,
          fixed: true,
          // antd's header checkbox only knows the page it can see. A reviewer
          // reads it as "all of them", so it fetches the whole filtered set
          // (to the batch ceiling) rather than silently taking twenty-five.
          onSelectAll: (selected: boolean) => {
            if (selected) selectAllMatchingMutation.mutate();
            else clearSelection();
          },
          selectedRowKeys: [...selection.keys()],
          onChange: (keys: Key[], rows: Record<string, unknown>[]) =>
            setSelection((current) => {
              const onPage = selectionFromRows(rows);
              return new Map(
                keys.map((key) => {
                  const itemId = Number(key);
                  return [
                    itemId,
                    onPage.get(itemId) ?? current.get(itemId) ?? "",
                  ];
                }),
              );
            }),
        }}
        onTableContextChange={setTableContext}
      />

      <ReviewItemDrawer
        trialId={trialId}
        itemId={openItemId}
        open={openItemId !== null}
        onClose={() => setOpenItemId(null)}
        onMark={(target) => markItems([target])}
        onReopen={(target) => reopenItems([target])}
        onOpenItem={(item) => setOpenItemId(item.id)}
        marking={isMarking}
      />
    </div>
  );
}
